import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { LinkedInApiProduct, Role } from '@bloomays-lib/types.shared';
import { SEND_LINKEDIN_MESSAGE } from '@bloomays-lib/adapter.api-talent';
import {
  AutoComplete,
  AutocompleteOption,
  Button,
  LoaderPending,
  TypeInput,
  ValidationTextField,
} from '@bloomays-lib/ui.shared';
import { useAuthStore } from '../../stores';
import { PageHeader } from '../molecules/PageHeader';
import { errorLogger } from '../../helpers/error';
import { notify } from '../../helpers/toastify';

type LinkedInMessageSent = {
  chatId: string;
  messageId: string;
};

type SendLinkedInMessageData = {
  sendLinkedInMessage: LinkedInMessageSent;
};

type UnipileAccountOption = AutocompleteOption & { linkedinApi: LinkedInApiProduct };

// Comptes Unipile de test configurés pour ce POC (TEC-3302) — pas encore persistés par Bloomie.
const UNIPILE_ACCOUNT_OPTIONS: UnipileAccountOption[] = [
  { id: 'GOVKaPDVSoS2wSbzKoyoUg', name: 'Joan', linkedinApi: LinkedInApiProduct.classic },
  { id: 'HeEWtvcvTYyFu10D8in6fw', name: 'Rodolphe', linkedinApi: LinkedInApiProduct.recruiter },
  { id: '9ZN6twGeQYqUZ-dNmktubg', name: 'Rosa', linkedinApi: LinkedInApiProduct.sales_navigator },
];

/**
 * TEC-3302 — Formulaire interne pour tester manuellement la mutation sendLinkedInMessage
 * (POC Unipile — envoi d'un message LinkedIn). Réservé au rôle System.
 */
const PocUnipile = () => {
  const { t } = useTranslation(['pocUnipile']);
  const auth = useAuthStore((state) => state.auth);
  const isSystem = auth?.user?.roles?.includes(Role.System) || false;

  const [account, setAccount] = useState<UnipileAccountOption | undefined>(undefined);
  const [talentLinkedInSlug, setTalentLinkedInSlug] = useState('');
  const [text, setText] = useState('');
  const [result, setResult] = useState<LinkedInMessageSent | null>(null);

  const [sendLinkedInMessage, { loading }] = useMutation<SendLinkedInMessageData>(SEND_LINKEDIN_MESSAGE, {
    context: { clientName: 'api.talents' },
    onCompleted: (data) => {
      setResult(data.sendLinkedInMessage);
      notify('success', t('sendSuccess'), null, { hideProgressBar: false, autoClose: 3000 });
    },
    onError: (mutationError) => {
      setResult(null);
      errorLogger(mutationError, { extraInfos: 'sendLinkedInMessage failed in PocUnipile' });
      notify('error', mutationError.message, mutationError, { hideProgressBar: true, autoClose: 5000 });
    },
  });

  if (!isSystem) {
    return <Navigate to="/forbidden/norole" replace />;
  }

  const isFormValid = account !== undefined && talentLinkedInSlug.trim() !== '' && text.trim() !== '';

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isFormValid || !account) return;
    void sendLinkedInMessage({
      variables: { accountId: account.id, talentLinkedInSlug, text, linkedinApi: account.linkedinApi },
    });
  };

  return (
    <Container>
      <PageHeader id="titlePocUnipile" title={t('title')} subtitle={t('subtitle')} />
      {loading && <LoaderPending text={t('sendingLoader')} />}
      <Form onSubmit={handleSubmit}>
        <AutoComplete<UnipileAccountOption>
          data={UNIPILE_ACCOUNT_OPTIONS}
          label={t('accountIdLabel')}
          value={account}
          setValue={setAccount}
        />
        <ValidationTextField
          name="talentLinkedInSlug"
          id="talentLinkedInSlug"
          label={t('talentLinkedInSlugLabel')}
          value={talentLinkedInSlug}
          type={TypeInput.text}
          size="small"
          required
          disabled={loading}
          onChange={(e) => setTalentLinkedInSlug(e.target.value)}
        />
        <ValidationTextField
          name="text"
          id="text"
          label={t('textLabel')}
          value={text}
          type={TypeInput.text}
          size="small"
          multiline
          minRows={3}
          required
          disabled={loading}
          onChange={(e) => setText(e.target.value)}
        />
        <ButtonsContainer>
          <Button
            textButton={t('submitButton')}
            buttonType="submit"
            disable={loading || !isFormValid}
            loading={loading}
          />
        </ButtonsContainer>
      </Form>
      {result && (
        <ResultBlock>
          <ResultTitle>{t('resultTitle')}</ResultTitle>
          <div>chatId : {result.chatId}</div>
          <div>messageId : {result.messageId}</div>
        </ResultBlock>
      )}
    </Container>
  );
};

export default PocUnipile;

const Container = styled('div')(() => ({
  margin: 'auto',
  width: '90%',
  maxWidth: 560,
}));

const Form = styled('form')(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const ButtonsContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '16px',
}));

const ResultBlock = styled('div')(({ theme }) => ({
  marginTop: '24px',
  padding: '16px',
  borderRadius: '12px',
  border: `1px solid ${theme.palette.neutral[20]}`,
  backgroundColor: theme.palette.neutral.white,
}));

const ResultTitle = styled('div')(() => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  marginBottom: '8px',
}));
